import React, { useState, useEffect, useRef } from "react";
import { notask } from "../assets/index";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiSearch,
  FiCalendar,
  FiRotateCcw,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiCheck,
  FiX,
  FiChevronDown,
  FiClipboard,
  FiClock,
  FiAlertTriangle,
} from "react-icons/fi";
import { IoIosArrowBack } from "react-icons/io";
import {
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import axios from "axios";

// react-date-range component & styles
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const STATUS_OPTIONS = ["pending", "inprocess", "completed"];
const PRIORITY_OPTIONS = ["Low", "Medium", "High"];
const ITEMS_PER_PAGE = 5;

const CreateTaskPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const email = localStorage.getItem("email");
  const trialStatus = localStorage.getItem("04-09-2025");

  // Determine active project ID from URL parameter or fallback to localStorage
  const activeProjectId = id || localStorage.getItem("project_id");

  const [tasks, setTasks] = useState([]); // Master list from API
  const [displayedTasks, setDisplayedTasks] = useState([]); // Filtered list
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [newTask, setNewTask] = useState({
    task_name: "",
    assign_to: "",
    description: "",
    priority: "Medium",
    due_date: "",
    status: "pending",
  });
  const [sharedUsers, setSharedUsers] = useState([]);
  const [error, setError] = useState(null);

  // --- Search & Filter States ---
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");

  // --- react-date-range State ---
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [dateRangeState, setDateRangeState] = useState([
    {
      startDate: firstDayOfMonth,
      endDate: lastDayOfMonth,
      key: "selection",
    },
  ]);
  const [isDateFilterActive, setIsDateFilterActive] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const datePickerRef = useRef(null);

  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);

  // --- MUI Snackbar & Dialog State ---
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [taskToDelete, setTaskToDelete] = useState(null);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Close date picker popup on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsDatePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync project_id to localStorage whenever URL param id changes
  useEffect(() => {
    if (id) {
      localStorage.setItem("project_id", id);
    }
  }, [id]);

  // Fetch initial tasks data
  const fetchTasks = async (projectIdToFetch) => {
    const targetId = projectIdToFetch || activeProjectId;
    if (!targetId || !email) return;

    try {
      const response = await fetch(
        "https://api.confidanto.com/task-backend/tasks",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_id: targetId,
            email: email,
          }),
        },
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchSharedUsers = async (projectIdToFetch) => {
    const targetId = projectIdToFetch || activeProjectId;
    if (!targetId || !email) return;

    try {
      const response = await fetch(
        "https://api.confidanto.com/projects-data/get-shared-project-email",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email,
            project_id: targetId,
          }),
        },
      );
      if (response.ok) {
        const users = await response.json();
        setSharedUsers(Array.isArray(users) ? users : []);
      }
    } catch (error) {
      console.error("Error fetching shared users:", error);
    }
  };

  useEffect(() => {
    const currentId = id || localStorage.getItem("project_id");
    if (email && currentId) {
      fetchTasks(currentId);
      fetchSharedUsers(currentId);
    }
  }, [id, email]);

  // Handle Date Range Selection
  const handleSelectDateRanges = (item) => {
    setDateRangeState([item.selection]);
    setIsDateFilterActive(true);
  };

  // Handle Filtering & Searching
  useEffect(() => {
    let filtered = [...tasks];

    // Search query
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          (t.task_name && t.task_name.toLowerCase().includes(q)) ||
          (t.description && t.description.toLowerCase().includes(q)) ||
          (t.assign_to && t.assign_to.toLowerCase().includes(q)),
      );
    }

    // Status filter
    if (selectedStatus !== "all") {
      filtered = filtered.filter(
        (t) =>
          t.status && t.status.toLowerCase() === selectedStatus.toLowerCase(),
      );
    }

    // Priority filter
    if (selectedPriority !== "all") {
      filtered = filtered.filter(
        (t) =>
          t.priority &&
          t.priority.toLowerCase() === selectedPriority.toLowerCase(),
      );
    }

    // Date range filter
    if (isDateFilterActive && dateRangeState[0]) {
      const { startDate, endDate } = dateRangeState[0];
      if (startDate && endDate) {
        const startStr = new Date(startDate).toISOString().split("T")[0];
        const endStr = new Date(endDate).toISOString().split("T")[0];

        filtered = filtered.filter((t) => {
          if (!t.due_date) return false;
          const taskDate = new Date(t.due_date).toISOString().split("T")[0];
          return taskDate >= startStr && taskDate <= endStr;
        });
      }
    }

    setDisplayedTasks(filtered);
    setCurrentPage(1); // Reset page on filter changes
  }, [
    tasks,
    searchQuery,
    selectedStatus,
    selectedPriority,
    isDateFilterActive,
    dateRangeState,
  ]);

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedStatus("all");
    setSelectedPriority("all");
    setIsDateFilterActive(false);
    setDateRangeState([
      {
        startDate: firstDayOfMonth,
        endDate: lastDayOfMonth,
        key: "selection",
      },
    ]);
  };

  const resetNewTaskForm = () => {
    setNewTask({
      task_name: "",
      assign_to: "",
      description: "",
      priority: "Medium",
      status: "pending",
      due_date: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTask((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditChange = (e, taskId) => {
    const { name, value } = e.target;
    setDisplayedTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.task_id === taskId ? { ...task, [name]: value } : task,
      ),
    );
  };

  // --- API Handlers ---
  const handleAddTask = async () => {
    if (!newTask.task_name.trim()) {
      showSnackbar("Please provide a task name", "error");
      return;
    }

    const targetId = activeProjectId || localStorage.getItem("project_id");
    const payload = {
      ...newTask,
      project_id: targetId,
      owner_email: email,
    };

    try {
      const response = await fetch(
        "https://api.confidanto.com/task-backend/create-task",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const data = await response.json();
      if (response.ok) {
        showSnackbar("Task created successfully!", "success");
        fetchTasks(targetId);
        setIsAddingTask(false);
        resetNewTaskForm();
      } else {
        throw new Error(data.message || "Unknown error");
      }
    } catch (error) {
      console.error("Error creating task:", error);
      showSnackbar(`Failed to create task: ${error.message}`, "error");
    }
  };

  const handleSaveEdit = (taskId) => {
    const task = displayedTasks.find((t) => t.task_id === taskId);
    if (!task) return;
    const payload = {
      ...task,
      due_date: task.due_date
        ? new Date(task.due_date).toISOString().split("T")[0]
        : null,
      owner_email: email,
    };
    fetch("https://api.confidanto.com/task-backend/update-task", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.message) {
          showSnackbar("Task updated successfully.", "success");
          setEditingTaskId(null);
          fetchTasks(activeProjectId);
        } else {
          throw new Error(data.error || data.message);
        }
      })
      .catch((error) => {
        console.error("Error updating task:", error);
        showSnackbar(`Failed to update task: ${error.message}`, "error");
      });
  };

  const updateTaskStatus = (e, taskId) => {
    const URL = "https://api.confidanto.com/task-backend/update-task-status";
    const newStatus = e.target.value;

    const reqBody = {
      task_id: taskId,
      status: newStatus,
      updated_by: localStorage.getItem("email"),
    };

    axios
      .post(URL, reqBody)
      .then(() => {
        showSnackbar("Task status updated", "success");
        fetchTasks(activeProjectId);
        setEditingTaskId(null);
      })
      .catch((err) => {
        console.error("Error updating task status:", err);
        showSnackbar("Failed to update task status", "error");
      });
  };

  const confirmDeleteTask = () => {
    if (!taskToDelete) return;
    fetch("https://api.confidanto.com/task-backend/delete-task", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ task_id: taskToDelete }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.message === "Task deleted successfully") {
          showSnackbar("Task deleted successfully.", "success");
          fetchTasks(activeProjectId);
        } else {
          throw new Error(data.message || "Failed to delete task");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        showSnackbar(`Deletion failed: ${error.message}`, "error");
      })
      .finally(() => {
        setTaskToDelete(null);
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  };

  // --- Badge Render Helpers ---
  const renderPriorityBadge = (priority) => {
    const p = (priority || "low").toLowerCase();
    if (p === "high") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-100">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
          High
        </span>
      );
    }
    if (p === "medium") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-100">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
          Medium
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-100">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
        Low
      </span>
    );
  };

  const renderStatusBadge = (status) => {
    const s = (status || "pending").toLowerCase();
    if (s === "completed") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-100">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
          Completed
        </span>
      );
    }
    if (s === "inprocess" || s === "in progress") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-100">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
          In Process
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-100">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
        Pending
      </span>
    );
  };

  // --- Task Analytics Calculations for Top Cards ---
  const totalTasksCount = tasks.length;
  const pendingCount = tasks.filter(
    (t) => (t.status || "").toLowerCase() === "pending",
  ).length;
  const completedCount = tasks.filter(
    (t) => (t.status || "").toLowerCase() === "completed",
  ).length;

  const todayStr = new Date().toISOString().split("T")[0];
  const overdueCount = tasks.filter((t) => {
    if (!t.due_date || (t.status || "").toLowerCase() === "completed")
      return false;
    const taskDate = new Date(t.due_date).toISOString().split("T")[0];
    return taskDate < todayStr;
  }).length;

  const pendingPercent = totalTasksCount
    ? Math.round((pendingCount / totalTasksCount) * 100)
    : 0;
  const completedPercent = totalTasksCount
    ? Math.round((completedCount / totalTasksCount) * 100)
    : 0;
  const overduePercent = totalTasksCount
    ? Math.round((overdueCount / totalTasksCount) * 100)
    : 0;

  // Pagination calculation
  const totalPages = Math.ceil(displayedTasks.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentItems = displayedTasks.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6">
      <div
        className={
          trialStatus === "Your trial has ended"
            ? "blur-sm pointer-events-none overflow-hidden"
            : ""
        }
      >
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-9 h-9 flex items-center justify-center bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-100 transition shadow-sm"
              >
                <IoIosArrowBack className="text-lg" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                  Tasks
                </h1>
                <p className="text-xs text-gray-500">
                  Stay organized and keep track of all your tasks in one place.
                </p>
              </div>
            </div>

            <button
              onClick={() => setIsAddingTask(true)}
              disabled={trialStatus === "Your trial has ended" || isAddingTask}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1B2A6B] hover:bg-[#152156] text-white text-sm font-medium rounded-lg shadow-sm transition disabled:opacity-50"
            >
              <FiPlus className="text-base" />
              Add Task
            </button>
          </div>

          {/* 4 Summary Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Total Tasks */}
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xl flex-shrink-0">
                  <FiClipboard />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">
                    Total Tasks
                  </p>
                  <p className="text-2xl font-bold text-gray-900 leading-tight">
                    {totalTasksCount}
                  </p>
                </div>
              </div>
              <div className="mt-5 space-y-1.5">
                <p className="text-[11px] text-gray-500 font-medium">
                  100% of tasks
                </p>
                <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                  <div className="bg-blue-600 h-full w-full rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Card 2: Pending */}
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center text-xl flex-shrink-0">
                  <FiClock />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Pending</p>
                  <p className="text-2xl font-bold text-gray-900 leading-tight">
                    {pendingCount}
                  </p>
                </div>
              </div>
              <div className="mt-5 space-y-1.5">
                <p className="text-[11px] text-gray-500 font-medium">
                  {pendingPercent}% of tasks
                </p>
                <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                  <div
                    className="bg-amber-500 h-full rounded-full transition-all duration-300"
                    style={{ width: `${pendingPercent}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Card 3: Completed */}
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl flex-shrink-0">
                  <FiCheck />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Completed</p>
                  <p className="text-2xl font-bold text-gray-900 leading-tight">
                    {completedCount}
                  </p>
                </div>
              </div>
              <div className="mt-5 space-y-1.5">
                <p className="text-[11px] text-gray-500 font-medium">
                  {completedPercent}% of tasks
                </p>
                <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                  <div
                    className="bg-emerald-600 h-full rounded-full transition-all duration-300"
                    style={{ width: `${completedPercent}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Card 4: Overdue */}
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center text-xl flex-shrink-0">
                  <FiAlertTriangle />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Overdue</p>
                  <p className="text-2xl font-bold text-gray-900 leading-tight">
                    {overdueCount}
                  </p>
                </div>
              </div>
              <div className="mt-5 space-y-1.5">
                <p className="text-[11px] text-gray-500 font-medium">
                  {overduePercent}% of tasks
                </p>
                <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                  <div
                    className="bg-rose-500 h-full rounded-full transition-all duration-300"
                    style={{ width: `${overduePercent}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-wrap items-center gap-4">
            {/* Search Input */}
            <div className="relative flex-1 min-w-[200px]">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-11 pl-9 pr-3 bg-white text-xs text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 placeholder-gray-400"
              />
            </div>

            {/* Status Dropdown */}
            <div className="relative min-w-[140px]">
              <label className="absolute -top-2.5 left-2.5 px-1.5 bg-white text-[11px] font-medium text-gray-500 z-10 pointer-events-none leading-none">
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full h-11 px-3 bg-white text-xs text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 capitalize cursor-pointer pt-1"
              >
                <option value="all">All Status</option>
                {STATUS_OPTIONS.map((st) => (
                  <option key={st} value={st}>
                    {st.charAt(0).toUpperCase() + st.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority Dropdown */}
            <div className="relative min-w-[140px]">
              <label className="absolute -top-2.5 left-2.5 px-1.5 bg-white text-[11px] font-medium text-gray-500 z-10 pointer-events-none leading-none">
                Priority
              </label>
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                className="w-full h-11 px-3 bg-white text-xs text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 cursor-pointer pt-1"
              >
                <option value="all">All Priority</option>
                {PRIORITY_OPTIONS.map((pr) => (
                  <option key={pr} value={pr}>
                    {pr}
                  </option>
                ))}
              </select>
            </div>

            {/* Due Date (react-date-range) Filter */}
            <div className="relative min-w-[210px]" ref={datePickerRef}>
              <label className="absolute -top-2.5 left-2.5 px-1.5 bg-white text-[11px] font-medium text-gray-500 z-10 pointer-events-none leading-none">
                Due Date
              </label>
              <button
                type="button"
                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                className="w-full h-11 px-3 bg-white text-xs text-left border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-600 flex items-center justify-between gap-2 pt-1 transition"
              >
                <div className="flex items-center gap-2 truncate">
                  <FiCalendar className="text-gray-400 text-sm flex-shrink-0" />
                  <span
                    className={
                      isDateFilterActive
                        ? "text-gray-800 font-medium truncate"
                        : "text-gray-400 truncate"
                    }
                  >
                    {isDateFilterActive
                      ? `${formatDate(dateRangeState[0].startDate)} - ${formatDate(
                          dateRangeState[0].endDate,
                        )}`
                      : "Select date range"}
                  </span>
                </div>
                <FiChevronDown className="text-gray-400 text-xs flex-shrink-0" />
              </button>

              {/* DateRangePicker Popover */}
              {isDatePickerOpen && (
                <div className="absolute right-0 top-12 mt-1 bg-white rounded-xl border border-gray-200 shadow-2xl p-3 z-50 animate-in fade-in zoom-in-95 duration-100">
                  <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-700">
                      Select Date Range
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setIsDateFilterActive(false);
                        setIsDatePickerOpen(false);
                      }}
                      className="text-xs text-gray-500 hover:text-red-600 transition"
                    >
                      Reset
                    </button>
                  </div>

                  <DateRangePicker
                    onChange={handleSelectDateRanges}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={1}
                    ranges={dateRangeState}
                    direction="horizontal"
                  />

                  <div className="flex justify-end pt-2 mt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setIsDatePickerOpen(false)}
                      className="px-4 py-1.5 bg-[#1B2A6B] text-white text-xs font-medium rounded-lg hover:bg-[#152156] transition"
                    >
                      Apply Range
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Clear Filters Button */}
            <button
              onClick={handleClearFilters}
              className="h-11 inline-flex items-center gap-1.5 px-4 border border-gray-300 hover:bg-gray-50 text-gray-600 text-xs font-medium rounded-lg transition"
            >
              <FiRotateCcw className="text-xs" />
              Clear Filters
            </button>
          </div>

          {/* Tasks Table Section */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            {tasks.length === 0 && !isAddingTask ? (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <img src={notask} alt="No tasks" className="mb-4 h-44 w-44" />
                <p className="text-gray-600 font-medium text-base">
                  You haven’t created any tasks for this project yet.
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Click on "Add Task" to get started.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#1C2D82] text-white">
                      <th className="py-3.5 px-4 font-semibold w-12 text-center">
                        #
                      </th>
                      <th className="py-3.5 px-4 font-semibold uppercase tracking-wider">
                        TASK NAME
                      </th>
                      <th className="py-3.5 px-4 font-semibold uppercase tracking-wider">
                        ASSIGN TO
                      </th>
                      <th className="py-3.5 px-4 font-semibold uppercase tracking-wider">
                        DESCRIPTION
                      </th>
                      <th className="py-3.5 px-4 font-semibold uppercase tracking-wider">
                        PRIORITY
                      </th>
                      <th className="py-3.5 px-4 font-semibold uppercase tracking-wider">
                        STATUS
                      </th>
                      <th className="py-3.5 px-4 font-semibold uppercase tracking-wider">
                        DUE DATE
                      </th>
                      <th className="py-3.5 px-4 font-semibold uppercase tracking-wider text-center">
                        ACTION
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700">
                    {/* Add Task Inline Form */}
                    {isAddingTask && (
                      <tr className="bg-indigo-50/50">
                        <td className="py-3 px-4 text-center font-medium text-indigo-600">
                          +
                        </td>
                        <td className="py-2 px-4">
                          <input
                            type="text"
                            name="task_name"
                            value={newTask.task_name}
                            onChange={handleInputChange}
                            placeholder="Task Name"
                            className="w-full px-2.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-indigo-600 bg-white"
                          />
                        </td>
                        <td className="py-2 px-4">
                          <select
                            name="assign_to"
                            value={newTask.assign_to}
                            onChange={handleInputChange}
                            className="w-full px-2.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-indigo-600 bg-white"
                          >
                            <option value="">Select user</option>
                            {sharedUsers.map((user, index) => (
                              <option key={index} value={user.email}>
                                {user.email}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-4">
                          <input
                            type="text"
                            name="description"
                            value={newTask.description}
                            onChange={handleInputChange}
                            placeholder="Description"
                            className="w-full px-2.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-indigo-600 bg-white"
                          />
                        </td>
                        <td className="py-2 px-4">
                          <select
                            name="priority"
                            value={newTask.priority}
                            onChange={handleInputChange}
                            className="w-full px-2.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-indigo-600 bg-white"
                          >
                            {PRIORITY_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-4">
                          <select
                            name="status"
                            value={newTask.status}
                            onChange={handleInputChange}
                            className="w-full px-2.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-indigo-600 capitalize bg-white"
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-4">
                          <input
                            type="date"
                            name="due_date"
                            value={newTask.due_date}
                            onChange={handleInputChange}
                            className="w-full px-2.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-indigo-600 bg-white"
                          />
                        </td>
                        <td className="py-2 px-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              onClick={handleAddTask}
                              title="Save Task"
                              className="p-1.5 text-emerald-600 hover:bg-emerald-50 border border-emerald-300 rounded transition"
                            >
                              <FiCheck className="text-sm" />
                            </button>
                            <button
                              onClick={() => {
                                setIsAddingTask(false);
                                resetNewTaskForm();
                              }}
                              title="Cancel"
                              className="p-1.5 text-gray-500 hover:bg-gray-100 border border-gray-300 rounded transition"
                            >
                              <FiX className="text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {currentItems.length === 0 && !isAddingTask ? (
                      <tr>
                        <td
                          colSpan="8"
                          className="text-center py-8 text-gray-400 font-medium"
                        >
                          No tasks match your filters.
                        </td>
                      </tr>
                    ) : (
                      currentItems.map((task, index) => {
                        const isEditing = editingTaskId === task.task_id;
                        const rowNumber = startIndex + index + 1;
                        const avatarLetter = (task.assign_to || "U")
                          .charAt(0)
                          .toUpperCase();

                        return (
                          <tr
                            key={task.task_id || index}
                            className="hover:bg-gray-50/75 transition-colors"
                          >
                            {/* # Index */}
                            <td className="py-3 px-4 text-center text-gray-500 font-medium">
                              {rowNumber}
                            </td>

                            {/* Task Name */}
                            <td className="py-3 px-4 font-medium text-gray-900">
                              {isEditing ? (
                                <input
                                  type="text"
                                  name="task_name"
                                  value={task.task_name || ""}
                                  onChange={(e) =>
                                    handleEditChange(e, task.task_id)
                                  }
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-indigo-600"
                                />
                              ) : (
                                task.task_name
                              )}
                            </td>

                            {/* Assign To with Avatar */}
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                                  {avatarLetter}
                                </span>
                                <span className="text-gray-600 truncate max-w-[180px]">
                                  {task.assign_to || "-"}
                                </span>
                              </div>
                            </td>

                            {/* Description */}
                            <td className="py-3 px-4 text-gray-600 max-w-[200px]">
                              {isEditing ? (
                                <input
                                  type="text"
                                  name="description"
                                  value={task.description || ""}
                                  onChange={(e) =>
                                    handleEditChange(e, task.task_id)
                                  }
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-indigo-600"
                                />
                              ) : (
                                <span className="truncate block">
                                  {task.description || "-"}
                                </span>
                              )}
                            </td>

                            {/* Priority */}
                            <td className="py-3 px-4">
                              {isEditing ? (
                                <select
                                  name="priority"
                                  value={task.priority || "Low"}
                                  onChange={(e) =>
                                    handleEditChange(e, task.task_id)
                                  }
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-indigo-600"
                                >
                                  {PRIORITY_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                renderPriorityBadge(task.priority)
                              )}
                            </td>

                            {/* Status */}
                            <td className="py-3 px-4">
                              {isEditing ? (
                                <select
                                  name="status"
                                  value={task.status || "pending"}
                                  onChange={(e) =>
                                    updateTaskStatus(e, task.task_id)
                                  }
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-indigo-600 capitalize"
                                >
                                  {STATUS_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                renderStatusBadge(task.status)
                              )}
                            </td>

                            {/* Due Date */}
                            <td className="py-3 px-4">
                              {isEditing ? (
                                <input
                                  type="date"
                                  name="due_date"
                                  value={formatDate(task.due_date)}
                                  onChange={(e) =>
                                    handleEditChange(e, task.task_id)
                                  }
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-indigo-600"
                                />
                              ) : (
                                <div className="flex items-center gap-1.5 text-gray-600">
                                  <FiCalendar className="text-gray-400 text-xs" />
                                  <span>
                                    {formatDate(task.due_date) || "-"}
                                  </span>
                                </div>
                              )}
                            </td>

                            {/* Action Buttons */}
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                {isEditing ? (
                                  <button
                                    onClick={() => handleSaveEdit(task.task_id)}
                                    title="Save"
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition"
                                  >
                                    <FiCheck className="text-sm" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() =>
                                      setEditingTaskId(task.task_id)
                                    }
                                    title="Edit"
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition"
                                  >
                                    <FiEdit className="text-sm" />
                                  </button>
                                )}

                                <button
                                  onClick={() => setTaskToDelete(task.task_id)}
                                  title="Delete"
                                  className="p-1.5 text-red-500 hover:bg-red-50 border border-red-200 rounded-lg transition"
                                >
                                  <FiTrash2 className="text-sm" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Footer */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white">
              <p className="text-xs text-gray-500 font-medium">
                Showing {displayedTasks.length > 0 ? startIndex + 1 : 0} to{" "}
                {Math.min(startIndex + ITEMS_PER_PAGE, displayedTasks.length)}{" "}
                of {displayedTasks.length} results
              </p>

              <div className="flex items-center gap-1">
                <button
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  className="w-7 h-7 flex items-center justify-center rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <FiChevronLeft className="text-xs" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-7 h-7 flex items-center justify-center rounded text-xs font-semibold transition ${
                        currentPage === page
                          ? "border border-indigo-600 text-indigo-600 bg-indigo-50/50"
                          : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  className="w-7 h-7 flex items-center justify-center rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <FiChevronRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trial Over Overlay */}
      {trialStatus === "Your trial has ended" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col justify-center items-center bg-white p-6 rounded-xl shadow-xl">
            <h1 className="text-xl font-semibold text-gray-900 mb-1">
              Your Free Trial has ended.
            </h1>
            <p className="text-sm text-gray-600 mb-4">
              Select your plan to continue to access your workspace.
            </p>
            <button
              className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm text-sm"
              onClick={() => navigate("/billing-support")}
            >
              Select Your Plan
            </button>
          </div>
        </div>
      )}

      {/* MUI Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* MUI Confirmation Dialog for Deleting Tasks */}
      <Dialog
        open={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Delete Task"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this task? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskToDelete(null)} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmDeleteTask} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateTaskPage;
